import React, { FC, MouseEvent, useState } from 'react';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { ITimesheetCellDTO } from 'src/api/dtos';
import { TIMESHEET_CELL_MENU_ICON } from 'src/features/timesheets/TimesheetGrid/TimesheetGrid.utils';
import { ChatSquareOutlinedIcon, DollarSignIcon, MoreVerticalIcon } from 'src/shared/components';
import { PermissionsEnums } from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useTranslate } from 'src/shared/providers';

interface Props {
  cell: ITimesheetCellDTO;
  disabled: boolean;
  hasInputExpense: boolean;
  onAddExpense: () => void;
  onAddNote: () => void;
}

export const TimesheetCellContextMenu: FC<Props> = ({ cell, disabled, hasInputExpense, onAddExpense, onAddNote }) => {
  const { t } = useTranslate();
  const [cellMenuElement, setCellMenuElement] = useState<null | HTMLElement>(null);
  const cellMenuOpen = Boolean(cellMenuElement);
  const { duration, expenses, note } = cell;

  const showContextMenu = !disabled && (duration || expenses.length || hasInputExpense);

  const handleCellMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setCellMenuElement(event.currentTarget);
  };

  const handleCellMenuClose = () => {
    setCellMenuElement(null);
  };

  const handleAddExpense = () => {
    onAddExpense();
    handleCellMenuClose();
  };

  const handleAddNote = () => {
    onAddNote();
    handleCellMenuClose();
  };

  return (
    <>
      {showContextMenu && (
        <IconButton
          size="small"
          sx={{
            position: 'absolute',
            right: '4px',
            top: '50%',
            transform: 'translate(0, -50%)',
            visibility: 'hidden',
          }}
          className={TIMESHEET_CELL_MENU_ICON}
          onClick={handleCellMenuOpen}
          tabIndex={-1}
        >
          <MoreVerticalIcon size={16} />
        </IconButton>
      )}
      <Menu
        anchorEl={cellMenuElement}
        open={cellMenuOpen}
        onClose={handleCellMenuClose}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        sx={{ mt: 1 }}
      >
        <ComponentWithPermissions permissions={PermissionsEnums.WTS_INPUT_EXPENSE}>
          <MenuItem onClick={handleAddExpense}>
            <ListItemIcon>
              <DollarSignIcon size={16} />
            </ListItemIcon>
            <Typography fontSize="0.875rem">
              {expenses.length ? t('timesheets.editExpenses') : t('timesheets.addExpenses')}
            </Typography>
          </MenuItem>
          {(duration || !!expenses.length) && <Divider />}
        </ComponentWithPermissions>
        {(duration || !!expenses.length) && (
          <MenuItem onClick={handleAddNote}>
            <ListItemIcon>
              <ChatSquareOutlinedIcon size={16} />
            </ListItemIcon>
            <Typography fontSize="0.875rem">{note ? t('global.editNote') : t('global.addANote')}</Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
